import { useRef, useState, useEffect, useMemo } from "react";
import { AppShell } from "../design-system/layouts/AppShell";
import { SplitPanel } from "../design-system/layouts/SplitPanel";
import { PageTabs } from "../design-system/primitives/PageTabs";
import { Button } from "../design-system/primitives/Button";
import { AxonMark } from "../design-system/primitives/AxonMark";
import { SignalsTable } from "../features/signals-table/SignalsTable";
import { IedPanel } from "../features/ied-panel/IedPanel";
import { useNbStore } from "../store/nbStore";
import { usePagesStore } from "../store/pagesStore";
import { useLayoutStore } from "../store/layoutStore";
import { useUiStore } from "../store/uiStore";
import { useLangStore } from "../store/langStore";
import { t as tr } from "../lib/i18n";
import { LAYOUT_TEMPLATES } from "../lib/jsonContracts";
import type { NbSignal } from "../lib/jsonContracts";
import { exportSignalsCsv, exportSignalsJson, exportSignalsXlsx, exportAG800 } from "../lib/exporters";
import { exportCorporateXlsx, exportCustomXlsx, type CorpTab } from "../lib/corporateExporter";
import { parseSignalList } from "../lib/referenceParsers";
import { predictBatch, validateInScl } from "../wasm/client";
import { GENERIC_NODES, brandFromModelKey, maxIndexOfType, modelKeyForIed, iedAdmiteMapeo } from "../lib/genericNodes";
import { Tutorial } from "../features/tutorial/Tutorial";
import { mapToScl, downloadScl, suggestDataset, listScl, suggestIed,
         DEFAULT_TRGOPS, DEFAULT_OPTFIELDS, type RcParams } from "../lib/sclMapper";
import { Modal } from "../design-system/primitives/Modal";

// ── Guía animada del formato propio: recorre Portada → Índice → Listado ──
function FormatoGuide() {
  const { lang } = useLangStore();
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStage(s => (s + 1) % 3), 2800);
    return () => clearInterval(t);
  }, []);
  const TABS = ["Portada", "Índice", "Listado de señales"];
  const Th = ({ children, req }: { children: React.ReactNode; req?: boolean }) => (
    <th className={"border border-[var(--color-outline-variant)] px-1.5 py-0.5 text-left font-medium " +
      (req ? "bg-[var(--color-brand-soft-20)] text-[var(--color-brand)] animate-pulse" : "text-[var(--color-on-surface-variant)]")}>
      {children}
    </th>
  );
  const Td = ({ children }: { children?: React.ReactNode }) => (
    <td className="border border-[var(--color-outline-variant)] px-1.5 py-0.5 text-[var(--color-on-surface-variant)] opacity-60">{children ?? "…"}</td>
  );
  return (
    <div className="rounded border border-[var(--color-outline-variant)] bg-[var(--color-surface)] p-2">
      <style>{`@keyframes corpFadeIn { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }`}</style>
      {/* pestañas del mini-libro */}
      <div className="mb-1.5 flex gap-1">
        {TABS.map((t, i) => (
          <span key={t} className={"rounded-t border border-b-0 px-2 py-0.5 text-[9px] transition-colors " +
            (i === stage
              ? "border-[var(--color-brand)] bg-[var(--color-brand-soft-20)] text-[var(--color-brand)] font-medium"
              : "border-[var(--color-outline-variant)] text-[var(--color-on-surface-variant)] opacity-60")}>
            {t}
          </span>
        ))}
      </div>
      <div key={stage} style={{ animation: "corpFadeIn .45s ease" }} className="min-h-[74px]">
        {stage === 0 && (
          <div className="flex h-[74px] flex-col items-center justify-center gap-1 rounded border border-dashed border-[var(--color-outline-variant)]">
            <span className="text-[11px] font-semibold tracking-wide text-[var(--color-on-surface)]">PORTADA</span>
            <span className="h-1 w-24 rounded bg-[var(--color-outline-variant)] opacity-50" />
            <span className="h-1 w-16 rounded bg-[var(--color-outline-variant)] opacity-40" />
            <span className="text-[9px] italic text-[var(--color-on-surface-variant)]">{tr("recomendada",lang)}</span>
          </div>
        )}
        {stage === 1 && (
          <table className="w-full border-collapse text-[9px]">
            <thead><tr><Th req>Hoja</Th><Th req>Descripción</Th></tr></thead>
            <tbody><tr><Td>01</Td><Td>Bahía de línea</Td></tr><tr><Td /><Td /></tr></tbody>
            <caption className="caption-bottom pt-1 text-left text-[9px] italic text-[var(--color-on-surface-variant)]">
              {tr("recomendada — columnas mínimas resaltadas",lang)}
            </caption>
          </table>
        )}
        {stage === 2 && (
          <table className="w-full border-collapse text-[9px]">
            <thead><tr><Th req>Equipo o IED</Th><Th req>Nombre de señal</Th><Th req>Tipo de dato</Th><Th req>Nodo MMS</Th><Th>…</Th></tr></thead>
            <tbody><tr><Td>REX640</Td><Td>Disparo 87B</Td><Td>SP</Td><Td>PROT/BBPDIF1/Op</Td><Td /></tr><tr><Td /><Td /><Td /><Td /><Td /></tr></tbody>
            <caption className="caption-bottom pt-1 text-left text-[9px] italic text-[var(--color-on-surface-variant)]">
              {tr("obligatoria — columnas mínimas resaltadas",lang)}
            </caption>
          </table>
        )}
      </div>
      <div className="mt-1.5 flex justify-center gap-1">
        {TABS.map((_, i) => (
          <span key={i} className={"h-1 w-1 rounded-full " + (i === stage ? "bg-[var(--color-brand)]" : "bg-[var(--color-outline-variant)]")} />
        ))}
      </div>
    </div>
  );
}

export function WorkspacePage() {
  const { pages, activeId, activate, close, rename, open, reset:resetPages } = usePagesStore();
  const { getTab, setTab, clearResults, validateExistingRows, addRows, removeTab } = useNbStore();
  const { layoutId, setLayout } = useLayoutStore();
  const [tutorial, setTutorial] = useState(false);
  const [glowTut, setGlowTut]   = useState(false);
  useEffect(() => {   // cada ingreso al espacio de trabajo: brillo temporal del botón Tutorial
    setGlowTut(true);
    const t = setTimeout(() => setGlowTut(false), 7000);
    return () => clearTimeout(t);
  }, []);
  const { theme, toggleTheme }  = useUiStore();
  const { lang, toggleLang }    = useLangStore();

  // C1 FIX: usar activeId directamente como tabId (nunca "default")
  const tabId     = activeId ?? "";
  const tabState  = getTab(tabId);
  const { rows, sclNodes, selectedIed, status } = tabState;

  const sclInputRef   = useRef<HTMLInputElement>(null);
  const extraInputRef = useRef<HTMLInputElement>(null);
  const [dlOpen,   setDlOpen]   = useState(false);
  const [genOpen,  setGenOpen]  = useState(false);
  const activeTitle = pages.find(p => p.id === activeId)?.title ?? "axon-tags";
  const [dlName, setDlName] = useState("");
  // Modal de seleccion de pestanas para exportacion corporativa
  const [corpOpen, setCorpOpen] = useState(false);
  const [corpSel, setCorpSel]   = useState<Set<string>>(new Set());
  // Sincronizar dlName con el título de la pestaña al abrir el dropdown
  const openDl = () => { setDlName(activeTitle); setDlOpen(v => !v); };

  // Abrir el modal corporativo: por defecto todas las pestañas seleccionadas
  const openCorpModal = () => {
    setCorpSel(new Set(pages.map(p => p.id)));
    setCorpOpen(true);
    setDlOpen(false);
  };

  // Ejecutar la exportación corporativa con las pestañas elegidas
  const [corpFormat, setCorpFormat] = useState<"axon"|"propio">("axon");
  const [corpTpl, setCorpTpl] = useState<{ bytes: ArrayBuffer; name: string } | null>(null);
  const corpTplInputRef = useRef<HTMLInputElement>(null);
  const runCorpExport = async () => {
    const tabs: CorpTab[] = pages
      .filter(p => corpSel.has(p.id))
      .map(p => {
        const st = getTab(p.id);
        return { id: p.id, title: p.title, ied: st.selectedIed,
                 rows: st.rows } as CorpTab;
      })
      .filter(t => t.rows.length > 0);
    if (tabs.length === 0) { alert(tr("Selecciona al menos una pestaña con señales.", useLangStore.getState().lang)); return; }
    try {
      if (corpFormat === "propio") {
        if (!corpTpl) { alert(tr("Carga primero tu formato XLSX.", useLangStore.getState().lang)); return; }
        await exportCustomXlsx(corpTpl.bytes, tabs, dlName || activeTitle);
      } else {
        await exportCorporateXlsx(tabs, dlName || activeTitle);
      }
      setCorpOpen(false);
    } catch (e) {
      alert((e instanceof Error ? e.message : String(e)));
    }
  };
  const hasSCL        = sclNodes.length > 0;
  const tpl           = LAYOUT_TEMPLATES.find(t=>t.id===layoutId) ?? LAYOUT_TEMPLATES[0];
  const goHome        = () => { resetPages(); };

  const handleWsSclFile = async (file: File) => {
    if (!tabId) return;
    await validateExistingRows(tabId, new Uint8Array(await file.arrayBuffer()), file.name);
  };

  // Toggle del botón "Validar nodos en SCL":
  // - sin SCL cargado → abre el diálogo de archivo
  // - con SCL cargado → quita la validación (limpia sclNodes y columnas) y libera el botón
  const handleToggleScl = () => {
    if (!tabId) return;
    if (hasSCL) {
      const cleared = rows.map(r => ({ ...r, iedName: undefined, dataset: undefined, status: undefined }));
      setTab(tabId, { rows: cleared, sclNodes: [], sclText: undefined, sclFileName: undefined });
    } else if (sclInputRef.current) {
      sclInputRef.current.value = "";
      sclInputRef.current.click();
    }
  };

  const handleExtraFile = async (file: File) => {
    if (!tabId) return;
    const bytes = new Uint8Array(await file.arrayBuffer());
    const ext   = file.name.toLowerCase();
    if (/\.(scd|cid|icd|iid|scl|xml)$/.test(ext)) {
      await validateExistingRows(tabId, bytes, file.name);
    } else {
      const parsed  = await parseSignalList(bytes, file.name);
      const newRows = await predictBatch(parsed.map(p=>p.signal), selectedIed);
      const final   = sclNodes.length ? await validateInScl(newRows, sclNodes) : newRows;
      addRows(tabId, final);
    }
  };

  // C7+8+9: actualizar filas directamente en el tab
  const handleRowsChange = (newRows: NbSignal[]) => {
    if (!tabId) return;
    setTab(tabId, { rows: newRows });
  };

  // Pegado masivo: signal puede traer varias líneas → una fila por línea no vacía
  const handleInferSignal = async (signal: string, insertAt: number) => {
    if (!tabId) return;
    const nombres = signal.split(/\r?\n|\t/).map(s => s.trim()).filter(s => s.length > 0);
    if (nombres.length === 0) return;
    const predicted = await predictBatch(nombres, selectedIed);
    const finalRows = sclNodes.length ? await validateInScl(predicted, sclNodes) : predicted;
    const newRows   = [...rows];
    newRows.splice(insertAt, 0, ...finalRows);
    setTab(tabId, { rows: newRows });
    setSelectedIdx(new Set());
  };

  // Editar nombre de señal existente y re-inferir su nodo
  const handleEditSignal = async (idx: number, nuevoNombre: string) => {
    if (!tabId) return;
    const nombre = nuevoNombre.trim(); if (!nombre) return;
    const [newRow] = await predictBatch([nombre], selectedIed);
    const finalRow = sclNodes.length ? (await validateInScl([newRow], sclNodes))[0] : newRow;
    const newRows  = [...rows];
    newRows[idx]   = { ...newRows[idx], signal: nombre, mmsNode: finalRow.mmsNode,
                       iedName: finalRow.iedName, dataset: finalRow.dataset, status: finalRow.status,
                       score: finalRow.score, topK: finalRow.topK };
    setTab(tabId, { rows: newRows });
  };

  // Editar manualmente el nodo MMS (sin re-inferir)
  const handleEditNode = (idx: number, nuevoNodo: string) => {
    if (!tabId) return;
    const newRows = [...rows];
    // Edición manual: el nodo ya no proviene del modelo — sin score de confianza.
    newRows[idx]  = { ...newRows[idx], mmsNode: nuevoNodo.trim(), score: undefined, topK: undefined };
    setTab(tabId, { rows: newRows });
  };

  // Triage: elegir una alternativa del top-3 — conserva el score de la opción elegida.
  const handlePickAlternative = (idx: number, label: string, score: number) => {
    if (!tabId) return;
    const newRows = [...rows];
    newRows[idx]  = { ...newRows[idx], mmsNode: label, score,
                      status: undefined, iedName: undefined, dataset: undefined };
    setTab(tabId, { rows: newRows });
  };

  // ── Mapeo a SCL (PROMPT_UNIVERSAL v2, solo MMS) ──
  const handleSetDsTarget = (idx: number, value: string) => {
    if (!tabId) return;
    const newRows = [...rows];
    newRows[idx] = { ...newRows[idx], dsTarget: value || undefined };
    setTab(tabId, { rows: newRows });
  };
  const [batchDs, setBatchDs] = useState("");
  const handleAssignDsBatch = () => {
    if (!tabId || !batchDs.trim()) return;
    const newRows = [...rows];
    for (const idx of selectedIdx) newRows[idx] = { ...newRows[idx], dsTarget: batchDs.trim() };
    setTab(tabId, { rows: newRows });
    setBatchDs(""); setSelectedIdx(new Set());
  };
  const { mapSclText, mapSclFileName, mapDatasets, mapIedTarget } = tabState;
  // Flecha temporal hacia "Mapear nodos a SCL" (viene del landing en modo Mapear)
  const [hintMap, setHintMap] = useState(false);
  useEffect(() => {
    if (!tabState.hintMapButton) return;
    setHintMap(true);
    if (tabId) setTab(tabId, { hintMapButton: false });   // consumir la bandera
    const t = setTimeout(() => setHintMap(false), 6000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabState.hintMapButton, tabId]);
  const mapLoaded = !!mapSclText;
  // Habilitación del mapeo:
  //  · Entrando por la pestaña "Mapear a SCL" del landing, la marca ya se eligió
  //    allí (mapBrandLock) y NO se exige seleccionar una referencia concreta.
  //  · Entrando directo al área de trabajo, hay que elegir una referencia de una
  //    marca que admita mapeo (SIEMENS SP5 / ABB-HITACHI; no SEL ni GE).
  const mapHabilitado = !!tabState.mapBrandLock || (!!selectedIed && iedAdmiteMapeo(selectedIed));
  const puedeMapear   = mapLoaded && mapHabilitado;
  const motivoBloqueo = mapHabilitado ? ""
    : (selectedIed ? tr("Los IEDs SEL y GE no admiten mapeo a SCL",lang)
                   : tr("Selecciona una referencia SIEMENS SP5 o ABB/HITACHI para mapear",lang));
  const dsOptions = useMemo(() => {
    const s = new Set<string>(["CTRL","PROT","MEAS","COMM","GEN"]);
    for (const d of mapDatasets ?? []) s.add(d);
    for (const r of rows) if (r.dsTarget) s.add(r.dsTarget);
    return [...s].sort();
  }, [mapDatasets, rows]);

  const mapSclInputRef = useRef<HTMLInputElement>(null);
  // Ventana emergente de selección de IED (un IED por pestaña)
  const [iedPicker, setIedPicker] = useState<null | {
    text: string; fileName: string;
    ieds: { name: string; lDevices: string[] }[]; datasets: string[]; chosen: string;
  }>(null);
  const handleMapSclFile = async (file: File) => {
    if (!tabId) return;
    try {
      const text = new TextDecoder().decode(new Uint8Array(await file.arrayBuffer()));
      const inv  = listScl(text);
      if (!inv.ieds.length) { alert(tr("El SCL no contiene elementos <IED>",lang)); return; }
      // Preselección: el IED sugerido para la primera señal con nodo (similitud LDevice)
      const primera = rows.find(r => r.mmsNode && r.mmsNode !== "N/A");
      const sugerido = primera ? suggestIed(primera.mmsNode, inv.ieds) : undefined;
      setIedPicker({ text, fileName: file.name, ieds: inv.ieds, datasets: inv.datasets,
                     chosen: sugerido ?? inv.ieds[0].name });
    } catch (e) {
      alert(tr("Error en el mapeo",lang) + ": " + (e instanceof Error ? e.message : String(e)));
    }
  };
  const confirmIedPicker = () => {
    if (!tabId || !iedPicker) return;
    setTab(tabId, { mapSclText: iedPicker.text, mapSclFileName: iedPicker.fileName,
                    mapIeds: iedPicker.ieds, mapDatasets: iedPicker.datasets,
                    mapIedTarget: iedPicker.chosen });
    setIedPicker(null);
  };
  const handleClearMapScl = () => {
    if (!tabId) return;
    setTab(tabId, { mapSclText: undefined, mapSclFileName: undefined, mapIeds: undefined, mapDatasets: undefined, mapIedTarget: undefined });
  };
  const [mapping, setMapping] = useState(false);
  // Diálogo de mapeo: pestañas Resumen / Parámetros de reporte
  const [mapDlg, setMapDlg] = useState<null | {
    view: "resumen" | "params";
    dsList: string[]; nuevos: string[]; existentes: string[];
    selDs: string; rcParams: Record<string, RcParams>; nRows: number;
  }>(null);
  const buildValidRows = () => rows
    .filter(r => r.mmsNode && r.mmsNode !== "N/A" && r.mmsNode !== "—")
    .map(r => ({ ...r, dsTarget: r.dsTarget || suggestDataset(r.mmsNode) }));
  const handleMapToScl = () => {
    if (!tabId || !mapSclText) return;
    if (!mapHabilitado) { alert(motivoBloqueo); return; }
    const validRows = buildValidRows();
    if (!validRows.length) { alert(tr("No hay señales con nodo MMS para mapear.",lang)); return; }
    const dsList = [...new Set(validRows.map(r => r.dsTarget!))].sort();
    const exist  = new Set(mapDatasets ?? []);
    const rcParams: Record<string, RcParams> = {};
    for (const d of dsList) rcParams[d] = { buffered: true, trgOps: { ...DEFAULT_TRGOPS }, optFields: { ...DEFAULT_OPTFIELDS } };
    setMapDlg({ view: "resumen", dsList,
                nuevos: dsList.filter(d => !exist.has(d)),
                existentes: dsList.filter(d => exist.has(d)),
                selDs: dsList[0], rcParams, nRows: validRows.length });
  };
  const toggleRcParam = (grupo: "trgOps" | "optFields", key: string) => {
    if (!mapDlg) return;
    const p = mapDlg.rcParams[mapDlg.selDs];
    setMapDlg({ ...mapDlg, rcParams: { ...mapDlg.rcParams,
      [mapDlg.selDs]: { ...p, [grupo]: { ...p[grupo], [key]: !p[grupo][key] } } } });
  };
  const confirmMapDlg = () => {
    if (!tabId || !mapSclText || !mapDlg) return;
    setMapping(true);
    try {
      const validRows = buildValidRows();
      const res = mapToScl(mapSclText, validRows, mapIedTarget, mapDlg.rcParams);
      const base = (mapSclFileName ?? "archivo.cid").replace(/(\.[^.]+)$/, "_mapeado$1");
      downloadScl(res.xml, base);
      const partes: string[] = [];
      for (const ir of res.iedResults) {
        partes.push(`IED ${ir.iedName} (LDevice ${ir.ldInst}):`);
        partes.push(`  ${tr("DataSets creados",lang)}: ${ir.createdDatasets.length ? ir.createdDatasets.join(", ") : "—"}`);
        partes.push(`  ${tr("DataSets ampliados",lang)}: ${ir.appendedDatasets.length ? ir.appendedDatasets.join(", ") : "—"}`);
        partes.push(`  FCDA ${tr("añadidos",lang)}: ${ir.fcdaCount}`);
      }
      if (res.skipped.length) partes.push(`${tr("Omitidas",lang)}: ${res.skipped.length} — ${res.skipped.slice(0,3).map(s=>s.signal+" ("+s.reason+")").join("; ")}${res.skipped.length>3?"…":""}`);
      alert(tr("Mapeo completado.",lang) + "\n\n" + partes.join("\n"));
      setMapDlg(null);
    } catch (e) {
      alert(tr("Error en el mapeo",lang) + ": " + (e instanceof Error ? e.message : String(e)));
    } finally { setMapping(false); }
  };

  // ── Selección múltiple (Ctrl/Cmd+clic) ──
  const [selectedIdx, setSelectedIdx] = useState<Set<number>>(new Set());
  const handleToggleSelect = (idx: number, _ctrl: boolean) => {
    setSelectedIdx(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };
  // Esc deselecciona todas las filas (sin tener que mantener Ctrl)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedIdx(prev => (prev.size ? new Set() : prev));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── Conversión a nodo genérico (una fila o varias seleccionadas) ──
  const modelKey = tabState.modelOverride || modelKeyForIed(selectedIed);

  // Aplica una conversión de tipo a un conjunto de índices, numerando en secuencia.
  // El siguiente índice = (máximo índice de ese tipo ya presente en la pestaña) + 1,
  // así respeta ediciones manuales que hayan saltado números (si existe Ind3 → siguiente Ind4).
  const applyGeneric = (indices: number[], typeId: string) => {
    if (!tabId || indices.length === 0) return;
    const brand = brandFromModelKey(modelKey); if (!brand) return;
    const tipo = GENERIC_NODES[brand].find(t => t.id === typeId); if (!tipo) return;
    const newRows = [...rows];
    for (const idx of indices) {
      // Recalcular el máximo en cada paso: las conversiones previas de este mismo
      // lote ya están en newRows, así cada fila toma un índice nuevo y único.
      const next = maxIndexOfType(newRows.map(r => r.mmsNode), tipo) + 1;
      newRows[idx] = { ...newRows[idx], mmsNode: tipo.gen(next), status: undefined, iedName: undefined, dataset: undefined, score: undefined, topK: undefined };
    }
    setTab(tabId, { rows: newRows });
  };
  const handleConvertGeneric = (idx: number, typeId: string) => applyGeneric([idx], typeId);
  const handleConvertGenericBatch = (typeId: string) => {
    const indices = [...selectedIdx].sort((a, b) => a - b);
    applyGeneric(indices, typeId);
    setSelectedIdx(new Set());
  };

  // C5 FIX: título de nueva pestaña con IED
  const handleNewTab = () => {
    const id    = `tab-${Date.now()}`;
    const title = selectedIed ? `${tr("Señales —",lang)} ${selectedIed}` : tr("Nueva pestaña",lang);
    open({ id, title, kind:"results" });
  };

  return (
    <AppShell
      topBar={
        <div className="flex items-center gap-3 px-3 py-2">
          <AxonMark />
          <span className="font-semibold text-[var(--color-brand)]">Axon Tags</span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline"
              onClick={()=>{ setTutorial(true); setGlowTut(false);
                try { localStorage.setItem("axon.tags.tutorial.seen","1"); } catch { /**/ } }}
              title={tr("Guía paso a paso de los botones principales",lang)}
              className={"py-1 px-2 text-xs " + (glowTut ? "animate-pulse ring-2 ring-[var(--color-brand)] rounded" : "")}>
              {tr("Tutorial",lang)}
            </Button>
            <select value={layoutId} onChange={(e)=>setLayout(e.target.value as typeof layoutId)}
              className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs">
              {LAYOUT_TEMPLATES.map(tp=><option key={tp.id} value={tp.id}>{tr(tp.label, lang)}</option>)}
            </select>
            <Button variant="ghost" onClick={toggleTheme}>{theme==="dark"?tr("Claro",lang):tr("Oscuro",lang)}</Button>
            <Button variant="outline" onClick={goHome}>{tr("Cerrar",lang)}</Button>
            <Button variant="outline" onClick={toggleLang} title={lang==="es"?"Switch interface to English":"Cambiar interfaz a español"} className="font-bold tracking-widest min-w-[44px]">{lang.toUpperCase()}</Button>
          </div>
        </div>
      }
      statusBar={
        <div className="flex gap-4">
          <span>{tr("Señales:",lang)} <b>{rows.length}</b></span>
          {hasSCL && <span>{tr("Presentes:",lang)} <b>{rows.filter(r=>r.status==="present").length}</b></span>}
          {hasSCL && <span>{tr("Ausentes:",lang)} <b>{rows.filter(r=>r.status==="absent").length}</b></span>}
          {status==="loading" && <span className="text-[var(--color-brand)]">{tr("Procesando…",lang)}</span>}
          {tabState.error && <span className="text-[var(--color-status-absent)] text-xs">{tr(tabState.error,lang)}</span>}
          <span className="ml-auto text-[10px] italic text-[var(--color-on-surface-variant)] opacity-70">
            {tr("El resultado se generó a partir de algoritmos de IA, puede cometer errores, por favor revisa", lang)}
          </span>
        </div>
      }
    >
      <div className="flex h-full flex-col">
        {/* C2 FIX: Tabs + botón + junto a la última pestaña */}
        <div className="flex items-stretch border-b border-[var(--color-border)] bg-[var(--color-surface-container-low)] overflow-x-auto">
          <PageTabs
            tabs={pages} activeId={activeId}
            onActivate={activate}
            onClose={id => { close(id); removeTab(id); }}
            onRename={rename}
          />
          <button onClick={handleNewTab}
            className="shrink-0 self-center mx-1 w-6 h-6 flex items-center justify-center rounded border border-[var(--color-border)] text-[var(--color-on-surface-variant)] hover:bg-[var(--color-surface-variant)] hover:text-[var(--color-on-surface)] text-sm leading-none"
            data-tutorial="new-tab" title={tr("Nueva pestaña",lang)}>+</button>
        </div>

        {/* Barra de acciones */}
        <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-surface-container-low)] px-3 py-1.5">
          <input ref={extraInputRef} type="file" accept=".xlsx,.xls,.csv,.txt,.scd,.cid,.icd,.iid,.scl,.xml"
            className="hidden" onChange={e=>{ const f=e.target.files?.[0]; if(f) void handleExtraFile(f); }} />
          <Button variant="ghost" data-tutorial="add-file" onClick={()=>{
            if(extraInputRef.current){ extraInputRef.current.value=""; extraInputRef.current.click(); }
          }} className="py-1 px-2 text-xs border border-[var(--color-border)]">
            {tr("+ Añadir",lang)}
          </Button>
          <Button variant="ghost" onClick={()=>clearResults(tabId)} className="py-1 px-2 text-xs border border-[var(--color-border)]">{tr("Limpiar",lang)}</Button>
          {/* Botón Descargar con dropdown — click-outside para cerrarlo */}
          <div className="relative">
            <Button variant="ghost" data-tutorial="download"
              onClick={openDl}
              className="py-1 px-2 text-xs border border-[var(--color-border)] flex items-center gap-1">
              {tr("⬇ Descargar",lang)} <span className="text-[9px]">▾</span>
            </Button>
            {dlOpen && (
              <>
                {/* Overlay transparente para cerrar al clic fuera */}
                <div className="fixed inset-0 z-40" onClick={() => setDlOpen(false)} />
                <div className="absolute left-0 top-full z-50 mt-0.5 rounded border border-[var(--color-border)] bg-[var(--color-surface-container)] shadow-lg py-2 min-w-[320px]">
                  {/* Campo de nombre editable */}
                  <div className="px-3 pb-2 border-b border-[var(--color-outline-variant)]">
                    <label className="block text-[10px] text-[var(--color-on-surface-variant)] mb-1 uppercase tracking-wide">
                      {tr("Nombre del archivo",lang)}
                    </label>
                    <input
                      value={dlName}
                      onChange={e => setDlName(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      onKeyDown={e => e.stopPropagation()}
                      className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-on-surface)] outline-none focus:border-[var(--color-brand)]"
                      placeholder={tr("Nombre del archivo…",lang)}
                    />
                  </div>
                  {/* Opciones de formato */}
                  <div className="pt-1">
                    {[
                      { label:"CSV",   fn:()=>void exportSignalsCsv(rows, dlName || activeTitle) },
                      { label:"JSON",  fn:()=>void exportSignalsJson(rows, dlName || activeTitle) },
                      { label:"XLSX",  fn:()=>void exportSignalsXlsx(rows, dlName || activeTitle) },
                      { label:tr("Listado de señales",lang), ext:"xlsx", fn:()=>openCorpModal() },
                      { label:"AG800", fn:()=>void exportAG800(rows, dlName || activeTitle) },
                    ].map(item => (
                      <button key={item.label}
                        onClick={(e) => { e.stopPropagation(); item.fn(); setDlOpen(false); }}
                        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-[var(--color-on-surface)] hover:bg-[var(--color-surface-variant)] transition-colors">
                        <span className="font-mono text-[var(--color-brand)] w-32 shrink-0 whitespace-nowrap">{item.label}</span>
                        <span className="text-[var(--color-on-surface-variant)] truncate text-[10px]">
                          {(dlName || activeTitle).substring(0, 24)}{((dlName || activeTitle).length > 24) ? "…" : ""}.{(item as {ext?:string}).ext ?? ((item.label === "AG800" || item.label.startsWith("XLSX")) ? "xlsx" : item.label.toLowerCase())}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          {/* Asignar DataSet en lote — cajita: Enter asigna a la selección */}
          {selectedIdx.size >= 1 && mapLoaded && (
            <div className="flex items-center rounded border border-[var(--color-outline-variant)] px-1 py-0.5">
              <input list="ds-target-opts" value={batchDs}
                onChange={(e) => setBatchDs(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleAssignDsBatch(); }}
                placeholder={`DataSet (${selectedIdx.size})`}
                title={tr("Escribe o elige un DataSet y pulsa Enter para asignarlo a las señales seleccionadas",lang)}
                className="w-28 rounded bg-transparent px-1.5 py-1 font-mono text-[10px] outline-none focus:bg-[var(--color-surface)]" />
            </div>
          )}
          {/* Botón Nodos Genéricos — visible con 2+ filas seleccionadas (Ctrl+clic) */}
          {selectedIdx.size >= 2 && (() => {
            const brand = brandFromModelKey(modelKey);
            const tipos = brand ? GENERIC_NODES[brand] : [];
            return (
              <div className="relative">
                <Button variant="ghost"
                  onClick={() => setGenOpen(v => !v)}
                  className="py-1 px-2 text-xs border border-[var(--color-brand-border-30)] text-[var(--color-brand)] flex items-center gap-1">
                  {tr("Nodos Genéricos",lang)} <span className="text-[9px]">({selectedIdx.size})</span> <span className="text-[9px]">▾</span>
                </Button>
                {genOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setGenOpen(false)} />
                    <div className="absolute left-0 top-full z-50 mt-0.5 rounded border border-[var(--color-border)] bg-[var(--color-surface-container)] shadow-lg py-1 min-w-[280px]">
                      {tipos.length === 0 ? (
                        <div className="px-3 py-1.5 text-[11px] text-[var(--color-on-surface-variant)] italic">
                          {tr("Selecciona un IED para ver opciones",lang)}
                        </div>
                      ) : tipos.map(t => (
                        <button key={t.id}
                          onClick={(e) => { e.stopPropagation(); handleConvertGenericBatch(t.id); setGenOpen(false); }}
                          className="block w-full text-left px-3 py-1.5 text-[11px] text-[var(--color-on-surface)] hover:bg-[var(--color-surface-variant)]">
                          {tr(t.label,lang)}
                          <span className="block font-mono text-[9px] text-[var(--color-on-surface-variant)]">{t.gen(1)}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <div className="ml-auto flex items-center gap-2">
            {selectedIed && <span className="text-xs text-[var(--color-on-surface-variant)]">{tr("IED:",lang)} <b className="text-[var(--color-brand)]">{selectedIed}</b></span>}
            <input ref={sclInputRef} type="file" accept=".scd,.cid,.icd,.iid,.scl,.xml" className="hidden"
              onChange={e=>{ const f=e.target.files?.[0]; if(f) void handleWsSclFile(f); }} />
            <Button data-tutorial="validate" variant="outline" onClick={handleToggleScl}
              className={"py-1 px-2 text-xs " + (hasSCL
                ? "bg-[var(--color-brand-soft-20)] border-[var(--color-brand)] text-[var(--color-brand)]"
                : "")}>
              {hasSCL ? "✓ "+tr("Validar nodos en SCL",lang) : tr("Validar nodos en SCL",lang)}
            </Button>
            {/* ── Cuadro de mapeo a SCL: Cargar SCL + Mapear nodos a SCL ── */}
            <div className="flex items-center gap-1 rounded border border-[var(--color-outline-variant)] px-1 py-0.5"
                 title={tr("Escribe los nodos en DataSets MMS del SCL cargado y descarga el archivo resultante",lang)}>
              <input ref={mapSclInputRef} type="file" accept=".scd,.cid,.icd,.iid,.scl,.xml" className="hidden"
                onChange={e=>{ const f=e.target.files?.[0]; if(f) void handleMapSclFile(f); }} />
              <Button variant="outline" disabled={!mapHabilitado} data-tutorial="load-scl"
                onClick={()=>{ if (mapSclInputRef.current) { mapSclInputRef.current.value=""; mapSclInputRef.current.click(); } }}
                className={"py-1 px-2 text-xs border-0 disabled:opacity-40 " + (mapLoaded
                  ? "bg-[var(--color-brand-soft-20)] text-[var(--color-brand)]" : "")}
                title={motivoBloqueo || (mapLoaded ? `${mapSclFileName ?? ""} — IED: ${mapIedTarget ?? ""}` : tr("Cargar el SCL donde se mapearán los nodos",lang))}>
                {mapLoaded ? "✓ "+tr("Cargar SCL",lang) : tr("Cargar SCL",lang)}
              </Button>
              {mapLoaded && mapIedTarget && (
                <span className="max-w-28 truncate font-mono text-[10px] text-[var(--color-brand)]">{mapIedTarget}</span>
              )}
              {mapLoaded && (
                <button onClick={handleClearMapScl} title={tr("Quitar SCL de mapeo",lang)}
                  className="px-0.5 text-[10px] text-[var(--color-on-surface-variant)] hover:text-[var(--color-status-absent)]">✕</button>
              )}
              <div className="relative">
                <Button variant="outline" data-tutorial="map-nodes" onClick={() => { setHintMap(false); handleMapToScl(); }} disabled={!puedeMapear || mapping}
                  title={motivoBloqueo || (!mapLoaded ? tr("Carga primero un SCL",lang) : "")}
                  className={"py-1 px-2 text-xs border-0 disabled:opacity-40 " + (hintMap ? "ring-2 ring-[var(--color-brand)] rounded" : "")}>
                  {mapping ? tr("Mapeando…",lang) : tr("Mapear nodos a SCL",lang)}
                </Button>
                {hintMap && (
                  <div className="pointer-events-none absolute right-1 top-full z-30 mt-1 flex flex-col items-center"
                       style={{ animation: "hintFloat 1s ease-in-out infinite" }}>
                    <style>{`@keyframes hintFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(4px); } }`}</style>
                    <span className="text-lg leading-none text-[var(--color-brand)]">↑</span>
                    <span className="mt-0.5 whitespace-nowrap rounded bg-[var(--color-brand)] px-2 py-0.5 text-[10px] font-medium text-black shadow">
                      {tr("Pulsa aquí para mapear",lang)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contenido */}
        <div className="min-h-0 flex-1">
          <SplitPanel direction={tpl.direction} autoSaveId={`axon-tags-transformer.split.${tpl.id}`}
            defaultSizes={tpl.defaultSizes}
            first={
              <SignalsTable
                rows={rows}
                selectedIed={selectedIed}
                sclNodes={sclNodes}
                modelKey={modelKey}
                selectedIdx={selectedIdx}
                onToggleSelect={handleToggleSelect}
                onRowsChange={handleRowsChange}
                onInferSignal={handleInferSignal}
                onEditSignal={handleEditSignal}
                onEditNode={handleEditNode}
                onConvertGeneric={handleConvertGeneric}
                onPickAlternative={handlePickAlternative}
                onSetDsTarget={handleSetDsTarget}
                dsOptions={dsOptions}
                mapEnabled={mapLoaded}
              />
            }
            second={<IedPanel tabId={tabId} sclDisabled={hasSCL} />}
          />
        </div>
      </div>

      {/* ── Modal de selección de pestañas para exportación corporativa ── */}
      {/* ── Ventana emergente: mapeo a SCL (Resumen · Parámetros de reporte) ── */}
      <Modal open={!!mapDlg} onClose={() => setMapDlg(null)}
             ariaLabel={tr("Mapear nodos a SCL",lang)} widthClass="w-[min(520px,94vw)]">
        {mapDlg && (
          <div className="flex flex-col">
            <div className="border-b border-[var(--color-border)] px-5 pt-5">
              <h2 className="text-base font-semibold text-[var(--color-on-surface)]">{tr("Mapear nodos a SCL",lang)}</h2>
              <p className="mt-0.5 text-[11px] text-[var(--color-on-surface-variant)]">
                {mapDlg.nRows} {tr("señales",lang)} · IED: <span className="font-mono text-[var(--color-brand)]">{mapIedTarget}</span>
              </p>
              <div className="mt-3 flex gap-4">
                {(["resumen","params"] as const).map(v => (
                  <button key={v} onClick={() => setMapDlg({ ...mapDlg, view: v })}
                    className={"pb-2 text-xs font-medium transition-colors " +
                      (mapDlg.view === v
                        ? "border-b-2 border-[var(--color-brand)] text-[var(--color-brand)]"
                        : "text-[var(--color-on-surface-variant)] hover:text-[var(--color-on-surface)]")}>
                    {v === "resumen" ? tr("Resumen",lang) : tr("Parámetros de reporte",lang)}
                  </button>
                ))}
              </div>
            </div>

            <div className="max-h-[52vh] overflow-y-auto px-5 py-4">
              {mapDlg.view === "resumen" && (
                <div className="flex flex-col gap-3 text-xs">
                  <div>
                    <span className="text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">{tr("DataSets a crear",lang)}</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {mapDlg.nuevos.length ? mapDlg.nuevos.map(d => (
                        <span key={d} className="rounded border border-[var(--color-brand-border-30)] bg-[var(--color-brand-soft-10)] px-2 py-0.5 font-mono text-[10px] text-[var(--color-brand)]">{d}</span>
                      )) : <span className="text-[var(--color-on-surface-variant)]">—</span>}
                    </div>
                  </div>
                  <div>
                    <span className="text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">{tr("DataSets existentes a ampliar",lang)}</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {mapDlg.existentes.length ? mapDlg.existentes.map(d => (
                        <span key={d} className="rounded border border-[var(--color-outline-variant)] px-2 py-0.5 font-mono text-[10px] text-[var(--color-on-surface-variant)]">{d}</span>
                      )) : <span className="text-[var(--color-on-surface-variant)]">—</span>}
                    </div>
                  </div>
                  <p className="text-[10px] leading-relaxed text-[var(--color-on-surface-variant)]">
                    {tr("Solo los DataSets nuevos reciben un ReportControl. Ajusta sus parámetros en la pestaña \"Parámetros de reporte\".",lang)}
                  </p>
                </div>
              )}

              {mapDlg.view === "params" && (
                mapDlg.nuevos.length === 0 ? (
                  <p className="py-6 text-center text-xs text-[var(--color-on-surface-variant)]">
                    {tr("No hay DataSets nuevos; no se generarán ReportControl.",lang)}
                  </p>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="mb-1 block text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">DataSet</label>
                      <select value={mapDlg.selDs}
                        onChange={(e) => setMapDlg({ ...mapDlg, selDs: e.target.value })}
                        className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5 font-mono text-xs outline-none focus:border-[var(--color-brand)]">
                        {mapDlg.nuevos.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div>
                      <span className="mb-1.5 block text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">{tr("Tipo de reporte",lang)}</span>
                      <div className="flex gap-1.5">
                        {([[true,"Buffered"],[false,"Unbuffered"]] as const).map(([val,label]) => (
                          <button key={label}
                            onClick={() => setMapDlg({ ...mapDlg, rcParams: { ...mapDlg.rcParams,
                              [mapDlg.selDs]: { ...mapDlg.rcParams[mapDlg.selDs], buffered: val } } })}
                            className={"flex-1 rounded border px-2 py-1.5 text-[11px] font-mono transition-colors " +
                              ((mapDlg.rcParams[mapDlg.selDs].buffered === val)
                                ? "border-[var(--color-brand)] bg-[var(--color-brand-soft-10)] text-[var(--color-brand)]"
                                : "border-[var(--color-outline-variant)] text-[var(--color-on-surface-variant)] hover:border-[var(--color-border)]")}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="mb-1.5 block text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">TrgOps · {tr("Opciones de disparo",lang)}</span>
                      <div className="grid grid-cols-2 gap-1.5">
                        {Object.keys(DEFAULT_TRGOPS).map(k => (
                          <label key={k} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-[var(--color-surface-variant)]">
                            <input type="checkbox" checked={mapDlg.rcParams[mapDlg.selDs].trgOps[k] ?? false}
                              onChange={() => toggleRcParam("trgOps", k)} className="accent-[var(--color-brand)]" />
                            <span className="font-mono text-[11px] text-[var(--color-on-surface)]">{k}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="mb-1.5 block text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">OptFields · {tr("Campos opcionales",lang)}</span>
                      <div className="grid grid-cols-2 gap-1.5">
                        {Object.keys(DEFAULT_OPTFIELDS).map(k => (
                          <label key={k} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-[var(--color-surface-variant)]">
                            <input type="checkbox" checked={mapDlg.rcParams[mapDlg.selDs].optFields[k] ?? false}
                              onChange={() => toggleRcParam("optFields", k)} className="accent-[var(--color-brand)]" />
                            <span className="font-mono text-[11px] text-[var(--color-on-surface)]">{k}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => setMapDlg({ ...mapDlg, rcParams: { ...mapDlg.rcParams,
                        [mapDlg.selDs]: { buffered: true, trgOps: { ...DEFAULT_TRGOPS }, optFields: { ...DEFAULT_OPTFIELDS } } } })}
                      className="self-start text-[10px] text-[var(--color-brand)] hover:underline">
                      {tr("Restaurar valores por defecto",lang)}
                    </button>
                  </div>
                )
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-[var(--color-border)] px-5 py-3">
              <Button variant="ghost" onClick={() => setMapDlg(null)} className="py-1.5 px-3 text-xs">{tr("Cancelar",lang)}</Button>
              <Button variant="primary" onClick={confirmMapDlg} disabled={mapping} className="py-1.5 px-3 text-xs">
                {mapping ? tr("Mapeando…",lang) : tr("Mapear y descargar",lang)}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Ventana emergente: selección de IED destino para el mapeo ── */}
      <Modal open={!!iedPicker} onClose={() => setIedPicker(null)}
             ariaLabel={tr("Selecciona el IED destino",lang)} widthClass="w-[min(420px,92vw)]">
        {iedPicker && (
          <div className="flex flex-col gap-4 p-6">
            <h2 className="pr-8 text-base font-semibold text-[var(--color-on-surface)]">
              {tr("Selecciona el IED destino",lang)}
            </h2>
            <p className="text-xs leading-relaxed text-[var(--color-on-surface-variant)]">
              {tr("Los nodos de esta pestaña se mapearán en los DataSets MMS del IED que elijas.",lang)}
              <span className="mt-1 block font-mono text-[10px] opacity-70">{iedPicker.fileName}</span>
            </p>
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wide text-[var(--color-on-surface-variant)]">IED</label>
              <select value={iedPicker.chosen}
                onChange={(e) => setIedPicker({ ...iedPicker, chosen: e.target.value })}
                className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-2 font-mono text-sm outline-none focus:border-[var(--color-brand)]">
                {iedPicker.ieds.map(i => <option key={i.name} value={i.name}>{i.name}</option>)}
              </select>
              {(() => {
                const sel = iedPicker.ieds.find(i => i.name === iedPicker.chosen);
                return sel && sel.lDevices.length ? (
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-[var(--color-on-surface-variant)] opacity-70">
                    LDevices: {sel.lDevices.slice(0, 8).join(" · ")}{sel.lDevices.length > 8 ? " …" : ""}
                  </p>
                ) : null;
              })()}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setIedPicker(null)} className="py-1.5 px-3 text-xs">
                {tr("Cancelar",lang)}
              </Button>
              <Button variant="primary" onClick={confirmIedPicker} className="py-1.5 px-3 text-xs">
                {tr("Confirmar",lang)}
              </Button>
            </div>
          </div>
        )}
      </Modal>
      {corpOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
          onClick={() => setCorpOpen(false)}>
          <div className="w-[420px] max-h-[80vh] overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-container)] shadow-xl"
            onClick={e => e.stopPropagation()}>
            <div className="border-b border-[var(--color-border)] px-4 py-3">
              <h3 className="text-sm font-semibold text-[var(--color-brand)]">{tr("Listado de señales",lang)}</h3>
              <p className="mt-0.5 text-[11px] text-[var(--color-on-surface-variant)]">
                {tr("Selecciona las pestañas a incluir. Se generará Portada + Índice + una hoja por pestaña.",lang)}
              </p>
            </div>
            <div className="max-h-[52vh] overflow-y-auto px-4 py-2">
              {/* ── Formato de salida ── */}
              <div className="mb-2 grid grid-cols-2 gap-2">
                {(["axon","propio"] as const).map(f => (
                  <button key={f} onClick={() => setCorpFormat(f)}
                    className={"rounded border px-2 py-1.5 text-left text-[11px] transition-colors " +
                      (corpFormat === f
                        ? "border-[var(--color-brand)] bg-[var(--color-brand-soft-10)] text-[var(--color-brand)]"
                        : "border-[var(--color-outline-variant)] text-[var(--color-on-surface-variant)] hover:border-[var(--color-border)]")}>
                    <span className="block font-medium">{f === "axon" ? tr("Formato Axon",lang) : tr("Formato propio",lang)}</span>
                    <span className="block text-[9px] opacity-70">
                      {f === "axon" ? tr("Plantilla corporativa por defecto",lang) : tr("Carga tu propio libro XLSX",lang)}
                    </span>
                  </button>
                ))}
              </div>
              {corpFormat === "propio" && (
                <div className="mb-3 flex flex-col gap-2">
                  <input ref={corpTplInputRef} type="file" accept=".xlsx" className="hidden"
                    onChange={async e => {
                      const f = e.target.files?.[0]; if (!f) return;
                      setCorpTpl({ bytes: await f.arrayBuffer(), name: f.name });
                    }} />
                  <div className="flex items-center gap-2">
                    <Button variant="outline" className="py-1 px-2 text-xs"
                      onClick={() => { if (corpTplInputRef.current) { corpTplInputRef.current.value=""; corpTplInputRef.current.click(); } }}>
                      {corpTpl ? "✓ " + tr("Cambiar formato",lang) : tr("Cargar formato XLSX",lang)}
                    </Button>
                    {corpTpl && <span className="truncate font-mono text-[10px] text-[var(--color-brand)]">{corpTpl.name}</span>}
                  </div>
                  <p className="text-[10px] leading-relaxed text-[var(--color-on-surface-variant)]">
                    {tr("Las hojas de listado de señales deben tener como mínimo las columnas \"Equipo o IED\" (se admite también \"Equipo\" o \"IED\"), \"Nombre de señal\", \"Tipo de dato\" y \"Nodo MMS\". Se recomienda incluir una hoja de portada y una hoja \"Índice\" con las columnas \"Hoja\" y \"Descripción\".",lang)}
                  </p>
                  <FormatoGuide />
                </div>
              )}
              {pages.length === 0 && (
                <p className="py-4 text-center text-xs text-[var(--color-on-surface-variant)]">{tr("No hay pestañas abiertas.",lang)}</p>
              )}
              {pages.map(p => {
                const st = getTab(p.id);
                const checked = corpSel.has(p.id);
                return (
                  <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-[var(--color-surface-variant)]">
                    <input type="checkbox" checked={checked}
                      onChange={() => setCorpSel(prev => {
                        const n = new Set(prev);
                        if (n.has(p.id)) n.delete(p.id); else n.add(p.id);
                        return n;
                      })}
                      className="accent-[var(--color-brand)]" />
                    <span className="flex-1 truncate text-xs">{p.title}</span>
                    <span className="text-[10px] text-[var(--color-on-surface-variant)]">
                      {st.rows.length} {tr("señales",lang)}{st.selectedIed ? ` · ${st.selectedIed}` : ""}
                    </span>
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-[var(--color-border)] px-4 py-3">
              <div className="flex gap-2 text-[11px]">
                <button onClick={() => setCorpSel(new Set(pages.map(p => p.id)))}
                  className="text-[var(--color-brand)] hover:underline">{tr("Todas",lang)}</button>
                <button onClick={() => setCorpSel(new Set())}
                  className="text-[var(--color-on-surface-variant)] hover:underline">{tr("Ninguna",lang)}</button>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setCorpOpen(false)} className="text-xs">{tr("Cancelar",lang)}</Button>
                <Button variant="primary" onClick={() => void runCorpExport()} className="text-xs">{tr("Exportar",lang)}</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tutorial guiado interactivo */}
      {tutorial && tabId && <Tutorial tabId={tabId} onClose={()=>setTutorial(false)} />}
    </AppShell>
  );
}
